// Browser entry. The page is pre-rendered HTML, so the first paint should not wait for the app:
// this module is tiny, lets the browser paint the frame it is parsing, then preloads the page's
// chunks (listed by the build in #eawb-chunks) and starts the app, which hydrates in place.
// `main.js` stays the app factory the build renders with.

// sessionStorage key shared with client/stale-assets.js and router/safe-import.js (frozen)
const RELOAD_GUARD_KEY = 'chunk_reload';

let started = false;

const start = () => {
  if (started) return;
  started = true;
  try {
    const chunks = JSON.parse(document.getElementById('eawb-chunks')?.textContent || '[]');
    for (const href of chunks) {
      const link = document.createElement('link');
      link.rel = 'modulepreload';
      link.crossOrigin = '';
      link.href = href;
      document.head.appendChild(link);
    }
  } catch {
    // The list is a hint; the import below fetches what it needs either way
  }
  import('@/app/main.js').catch(reloadOnce);
};

// The app chunk is gone: the HTML predates a deploy. Reload once for the new assets (the same
// guard the app uses once it runs); a second failure stays put rather than looping.
const reloadOnce = () => {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  } catch {
    return;
  }
  window.location.reload();
};

// After the next frame has painted. A hidden tab gets no frames, so it starts right away, and a
// timer covers a frame that never comes.
const afterFirstPaint = () => {
  if (document.visibilityState === 'hidden') return start();
  const prerendered = document.getElementById('app')?.hasAttribute('data-server-rendered');
  if (prerendered && PerformanceObserver.supportedEntryTypes?.includes('paint')) {
    new PerformanceObserver((list, observer) => {
      if (!list.getEntriesByName('first-contentful-paint').length) return;
      observer.disconnect();
      start();
    }).observe({ type: 'paint', buffered: true });
    document.addEventListener('visibilitychange', start, { once: true });
    setTimeout(start, 3000);
  } else {
    requestAnimationFrame(() => setTimeout(start, 0));
    setTimeout(start, 1000);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', afterFirstPaint, { once: true });
} else {
  afterFirstPaint();
}
